import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { TrustBadge } from '../components/brand/TrustBadge';
import { MarketplaceDisclaimer } from '../components/brand/MarketplaceDisclaimer';
import {
  CheckCircle2, Loader2, ShieldCheck, ArrowRight, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;
const POLL_INTERVAL = 2500;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const offerId = params.get('offer_id');
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  const [status, setStatus] = useState('checking');
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data: d } = await axios.get(`${API}/payments/status/${sessionId}`, getAuthHeader());
      setData(d);
      if (d.payment_status === 'paid') {
        setStatus('success');
      } else if (['expired', 'failed'].includes(d.payment_status)) {
        setStatus('failed');
      } else if (attempts + 1 >= MAX_POLLS) {
        setStatus('timeout');
      } else {
        setAttempts((a) => a + 1);
      }
    } catch (e) {
      if (attempts + 1 >= MAX_POLLS) setStatus('failed');
      else setAttempts((a) => a + 1);
    }
  }, [sessionId, attempts, getAuthHeader]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (status !== 'checking') return;
    if (attempts === 0) return;
    const t = setTimeout(poll, POLL_INTERVAL);
    return () => clearTimeout(t);
  }, [attempts, status, poll]);

  return (
    <div className="min-h-screen bg-cn-bg flex flex-col" data-testid="checkout-success-page">
      <header className="border-b border-cn-border bg-white">
        <div className="cn-container h-16 flex items-center justify-between">
          <Link to="/" data-testid="success-home-link"><CaraLoomLogo /></Link>
          <Link to="/patient/offers" className="cn-btn cn-btn-ghost text-sm">My offers</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full">
          {status === 'checking' && (
            <div className="cn-card p-10 text-center" data-testid="checkout-checking">
              <Loader2 className="w-10 h-10 text-cn-primary animate-spin mx-auto mb-5" />
              <h1 className="font-display text-2xl font-bold mb-2">Confirming your payment…</h1>
              <p className="text-cn-text-muted text-sm">This takes a few seconds. Please don't close this page.</p>
              <div className="mt-6 h-1.5 rounded-full bg-cn-bone-200 overflow-hidden">
                <div
                  className="h-full bg-cn-primary transition-all duration-500"
                  style={{ width: `${Math.min(100, (attempts + 1) * (100 / MAX_POLLS))}%` }}
                />
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="cn-card p-10 text-center" data-testid="checkout-success">
              <div className="w-16 h-16 rounded-full bg-cn-sage-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-9 h-9 text-cn-primary" />
              </div>
              <h1 className="font-display text-3xl font-bold mb-2">Payment secured</h1>
              <p className="text-cn-text-muted">
                Your funds are secured up-front and scheduled for payout once your clinician
                signs their visit note. CaraLoom uses Stripe Connect to facilitate the direct
                clinician-to-family transaction — we never hold your money.
              </p>
              <div className="mt-6 rounded-2xl bg-cn-bone-100 p-5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-cn-text-muted">Amount</span>
                  <span className="font-display font-bold text-lg">${data?.amount?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mt-2 text-sm">
                  <span className="text-cn-text-muted">Status</span>
                  <TrustBadge variant="encrypted" label="Awaiting service verification" />
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-2">
                <button
                  onClick={() => navigate('/patient/offers')}
                  className="cn-btn cn-btn-primary"
                  data-testid="success-view-bookings"
                >
                  View my bookings <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/patient/dashboard')}
                  className="cn-btn cn-btn-ghost"
                  data-testid="success-back-dashboard"
                >
                  Back to dashboard
                </button>
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs text-cn-text-muted justify-center">
                <ShieldCheck className="w-3.5 h-3.5 text-cn-primary" /> Secured by Stripe Connect · Direct clinician-to-family settlement
              </div>
            </div>
          )}

          {(status === 'failed' || status === 'timeout') && (
            <div className="cn-card p-10 text-center" data-testid="checkout-failed">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
                <AlertTriangle className="w-9 h-9 text-red-600" />
              </div>
              <h1 className="font-display text-2xl font-bold mb-2">
                {status === 'timeout' ? 'Still processing…' : 'We could not confirm your payment'}
              </h1>
              <p className="text-cn-text-muted text-sm">
                {status === 'timeout'
                  ? 'Your payment may still complete. Check your offers in a moment.'
                  : 'Please try again, or contact support if you were charged.'}
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <button
                  onClick={() => navigate(offerId ? '/patient/offers' : '/patient/dashboard')}
                  className="cn-btn cn-btn-primary"
                  data-testid="failed-back-button"
                >
                  Back to my offers
                </button>
              </div>
            </div>
          )}

          <div className="mt-6">
            <MarketplaceDisclaimer variant="micro" />
          </div>
        </div>
      </main>
    </div>
  );
}
